<template>
    <section class="rta-list-container">
        <b-overlay :show="loadingRtaList" no-wrap opacity="1">
        </b-overlay>
        <PageTitle :main="$t('rtaList')" sub="" imagem="rta/rtatable.svg" />
        <section class="rta-content-wrapper">
            <section class="filters-and-actions">
                <div class="filters-wrapper">
                    <Filters @filter-changed="loadRtaList" @filter-reset="resetFilters" :headerToExport="tableColumns" :dataToExport="tableData" fileName="Lista de RTAs" :showExport="false" ref="tableOpenRta" highlightSelect />
                </div>
                <div class="right-buttons">
                    <button v-if="canBulkCancel" @click="openCancelModal" class="btn-cancel-selected" :disabled="selectedRtas.length === 0">
                        <b-icon-trash-fill />
                        <span>Cancelar RTA Selecionada{{ selectedRtas.length > 1 ? 's' : '' }}{{ selectedRtas.length > 0 ? ` (${selectedRtas.length})` : '' }}</span>
                    </button>
                    <button @click="exportExcel" class="btn-export">
                        <b-icon-file-earmark-excel-fill />
                        <span>Exportar Excel</span>
                    </button>
                    <button @click="exportPdf" class="btn-export">
                        <b-icon-file-earmark-pdf-fill />
                        <span>Exportar PDF</span>
                    </button>
                </div>
            </section>
            <section class="table-section">
                <Table ref="tableRta" :tableColumns="tableColumns" :tableData="tableData" :dbClickFunc="openRtaDetails" :openInNewTabFunc="openRtaInNewTab" :hideColsArr="hideCols" :bottomOffset="115" @column-filters-changed="saveColumnFilters" />
            </section>
            <section class="overview-section" :class="{ 'no-data': tableData.length === 0 }">
                <Overview :filters="overviewFilters" />
            </section>
        </section>
        <b-modal id="rtaDetailsModal">
            <span>{{ this.rtaDetails.code }}</span>
        </b-modal>

        <b-modal id="cancelRtasModal" title="Confirmar Cancelamento" hide-footer>
            <p><strong>Tem certeza que deseja cancelar {{ selectedRtas.length }} RTA{{ selectedRtas.length > 1 ? 's' : '' }}?</strong></p>
            <p v-if="selectedRtas.length > 0">RTAs selecionadas:</p>
            <ul v-if="selectedRtas.length > 0 && selectedRtas.length <= 10">
                <li v-for="rtaId in selectedRtas" :key="rtaId">
                    {{ getRtaCodeById(rtaId) }}
                </li>
            </ul>
            <p v-else-if="selectedRtas.length > 10">{{ selectedRtas.length }} RTAs selecionadas</p>
            <div class="modal-actions">
                <button @click="cancelSelectedRtas" class="btn-confirm-cancel">Confirmar Cancelamento</button>
                <button @click="$bvModal.hide('cancelRtasModal')" class="btn-modal-close">Fechar</button>
            </div>
        </b-modal>
    </section>
</template>

<script>
import axios from "axios";
import PageTitle from "../template/PageTitle.vue";
import Filters from "./commons/Filters.vue";
import Table from "./commons/Table.vue";
import Overview from "./commons/Overview.vue";
import { defaultSuccess, showError, urlMainAPI } from '../../global';
import * as moment from 'moment'
import { mapState } from "vuex";

export default {
    name: "ConsultarRTA",
    components: { PageTitle, Filters, Table, Overview },
    data() {
        return {
            loadingRtaList: false,
            rtaDetails: {},
            hideCols: [],
            columnFiltersStorageKey: 'rta-list-column-filters',
            selectedRtas: [],
            selectAll: false,
            canBulkCancel: false,
            defaultFilters: {
                processId: null,
                workshiftId: null,
                startDate: "",
                endDate: "",
                indicatorId: null,
            },
            overviewFilters: null,
            statusColors: {
                "Aberta":               "#1976D2",
                "Em Atraso":            "#E65100",
                "Em Análise":           "#1976D2",
                "Análise em Atraso":    "#F57C00",
                "Em Ações":             "#388E3C",
                "Em Ações com Atraso":  "#E65100",
                "Avaliação Pendente":   "#7B1FA2",
                "Concluída":            "#388E3C",
                "Concluída com Atraso": "#E65100",
                "Cancelada":            "#757575",
                "Recusada":             "#D32F2F",
            },
            tableColumns: [
                {
                    field: "selected",
                    key: 'selected',
                    title: '',
                    align: "center",
                    sortBy: false,
                    width: 45,
                    filter: false,
                    renderHeaderCell: (column, h) => {
                        return h('input', {
                            attrs: {
                                type: 'checkbox'
                            },
                            domProps: {
                                checked: this.selectAll
                            },
                            on: {
                                change: (event) => {
                                    this.toggleSelectAll(event.target.checked);
                                }
                            }
                        });
                    },
                    renderBodyCell: ({ row }, h) => {
                        return h('input', {
                            attrs: {
                                type: 'checkbox'
                            },
                            domProps: {
                                checked: this.selectedRtas.includes(row.id)
                            },
                            on: {
                                change: (event) => {
                                    event.stopPropagation();
                                    this.toggleRtaSelection(row.id, event.target.checked);
                                }
                            }
                        });
                    }
                },
                { field: "code", key: 'code', title: 'Código RTA', align: "center", sortBy: "", width: 90, ellipsis: { lineClamp: 1 } },
                { field: "formattedCreationDate", key: 'formattedCreationDate', title: 'Data de Abertura', align: "center", sortBy: "", width: 130, ellipsis: { lineClamp: 1 } },
                {
                    field: "status", key: 'status', title: 'Status', align: "center", sortBy: "", width: 150,
                    renderBodyCell: ({ row }, h) => {
                        const color = this.statusColors[row.status] || "#666";
                        return h('span', {
                            style: `display:inline-block; padding:0.15rem 0.5rem; border-radius:0.8rem;
                                    background:${color}26; color:${color}; font-weight:500; font-size:0.8rem; white-space:nowrap;`
                        }, row.status || '-');
                    },
                    renderHeaderCell: (column, h) => {
                        const tooltip = [
                            '<b>Aberta</b>: RTA recém-criada, sem análise ou ações',
                            '<b>Em Atraso</b>: Aberta e passou do prazo',
                            '<b>Em Análise</b>: Análise em andamento dentro do prazo',
                            '<b>Análise em Atraso</b>: Análise em andamento fora do prazo',
                            '<b>Em Ações</b>: Plano de ação em andamento dentro do prazo',
                            '<b>Em Ações com Atraso</b>: Plano de ação em andamento fora do prazo',
                            '<b>Em Andamento</b>: RTA em andamento (análise ou ações)',
                            '<b>Avaliação Pendente</b>: Aguardando avaliação de eficácia',
                            '<b>Concluída</b>: Concluída dentro do prazo',
                            '<b>Concluída com Atraso</b>: Concluída após o prazo',
                            '<b>Cancelada</b>: RTA cancelada',
                            '<b>Recusada</b>: RTA recusada'
                        ].join('<br>');
                        return h('span', { style: 'display:inline-flex;align-items:center;gap:4px;' }, [
                            'Status',
                            h('span', {
                                style: 'cursor:pointer;color:#FF5F00;font-size:0.85rem;line-height:1;',
                                attrs: { title: '' },
                                directives: [{ name: 'b-tooltip', value: { title: tooltip, placement: 'right', html: true } }]
                            }, ' ⓘ')
                        ]);
                    }
                },
                { field: "indicator", key: 'indicator', title: 'Indicador', align: "center", sortBy: "", width: 140, ellipsis: { lineClamp: 1 } },
                { field: "process", key: 'process', title: 'Processo', align: "center", sortBy: "", width: 120, ellipsis: { lineClamp: 1 } },
                { field: "trigger", key: 'trigger', title: 'Gatilho', align: "center", sortBy: "", width: 90, ellipsis: { lineClamp: 1 } },
                { field: "responsible", key: 'responsible', title: 'Responsável', align: "center", sortBy: "", width: 140, ellipsis: { lineClamp: 1 } },
                { field: "workshift", key: 'workshift', title: 'Turno', align: "center", sortBy: "", width: 90, ellipsis: { lineClamp: 1 } },
                { field: "relatedfca", key: 'relatedfca', title: 'FCAs Abertas', align: "center", sortBy: "", width: 110, ellipsis: { lineClamp: 1 } },
                { field: "efficiencyrta", key: 'efficiencyrta', title: 'Eficácia da RTA', align: "center", sortBy: "", width: 130, ellipsis: { lineClamp: 1 } },
                { field: "efficiencyaction", key: 'efficiencyaction', title: 'Efic. Plano de Ação', align: "center", sortBy: "", width: 140, ellipsis: { lineClamp: 1 } },
                { field: "formattedDeadline", key: 'formattedDeadline', title: 'Prazo', align: "center", sortBy: "", width: 110, ellipsis: { lineClamp: 1 } },
                {
                    field: "actions",
                    key: 'actions',
                    title: 'Ações',
                    align: "center",
                    sortBy: false,
                    width: 70,
                    filter: false,
                    renderBodyCell: ({ row }, h) => {
                        return h('i', {
                            class: 'fa fa-external-link open-new-tab-icon',
                            attrs: {
                                title: 'Abrir em nova aba'
                            },
                            on: {
                                click: (event) => {
                                    event.stopPropagation();
                                    this.openRtaInNewTab(row);
                                }
                            }
                        });
                    }
                },
            ],
            tableData: [],
        }
    },
    methods: {
        async resetFilters() {
            if (this.$route.params.indicatorId) {
                this.$router.push({ name: 'rtalist' });
                this.defaultFilters = { ...this.defaultFilters, indicatorId: null };
            }

           this.$refs.tableRta?.resetAllColumnFilters();
           this.clearColumnFilters();
           await this.loadRtaList(this.defaultFilters);
        },

        validatePermissions() {
            const permissions = this.user?.Permissions;

            // Verifica se o usuário tem a permissão de cancelamento em massa de RTAs
            if (permissions?.rtacancelar && permissions.rtacancelar.allowed === true) {
                this.canBulkCancel = true;
            }
        },

        toggleRtaSelection(rtaId, checked) {
            if (checked) {
                if (!this.selectedRtas.includes(rtaId)) {
                    this.selectedRtas.push(rtaId);
                }
            } else {
                this.selectedRtas = this.selectedRtas.filter(id => id !== rtaId);
                this.selectAll = false;
            }
        },

        toggleSelectAll(checked) {
            this.selectAll = checked;
            if (checked) {
                this.selectedRtas = this.tableData.map(rta => rta.id);
            } else {
                this.selectedRtas = [];
            }
        },

        openCancelModal() {
            if (this.selectedRtas.length === 0) {
                this.$bvToast.toast("Selecione pelo menos uma RTA para cancelar.", {
                    title: "Nenhuma RTA selecionada",
                    variant: "warning",
                    toaster: "b-toaster-bottom-left",
                    solid: true
                });
                return;
            }
            this.$bvModal.show('cancelRtasModal');
        },

        getRtaCodeById(rtaId) {
            const rta = this.tableData.find(r => r.id === rtaId);
            return rta ? rta.code : `ID: ${rtaId}`;
        },

        async cancelSelectedRtas() {
            this.$bvModal.hide('cancelRtasModal');

            try {
                const userName = this.user?.Name || 'Sistema';
                const response = await axios.post(`${urlMainAPI}/cancelrtas/${userName}`, {
                    rtaIds: this.selectedRtas
                });

                if (response.status === 200) {
                    defaultSuccess(`${this.selectedRtas.length} RTA${this.selectedRtas.length > 1 ? 's' : ''} cancelada${this.selectedRtas.length > 1 ? 's' : ''} com sucesso!`);
                    this.selectedRtas = [];
                    this.selectAll = false;

                    // Aguarda um pouco para garantir que o backend commitou as mudanças
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Simula clique no botão buscar para recarregar tudo corretamente
                    this.$refs.tableOpenRta.emitFilters();
                }
            } catch (error) {
                showError('Falha ao cancelar RTAs.');
                console.error(error);
            }
        },

        exportExcel() {
            this.$refs.tableOpenRta.exportExcel();
        },

        exportPdf() {
            this.$refs.tableOpenRta.exportPdf();
        },

        saveColumnFilters(columnFilters) {
            try {
                // Não salva filtros de coluna se houver indicatorId na rota (vindo do monitoramento)
                if (this.$route.params.indicatorId) {
                    return;
                }

                localStorage.setItem(this.columnFiltersStorageKey, JSON.stringify(columnFilters));
            } catch (error) {
                console.error('Erro ao salvar filtros de coluna no localStorage:', error);
            }
        },

        loadColumnFilters() {
            try {
                const savedFilters = localStorage.getItem(this.columnFiltersStorageKey);
                if (savedFilters) {
                    const filters = JSON.parse(savedFilters);
                    this.$nextTick(() => {
                        if (this.$refs.tableRta) {
                            this.$refs.tableRta.loadColumnFilters(filters);
                        }
                    });
                    return true;
                }
            } catch (error) {
                console.error('Erro ao carregar filtros de coluna do localStorage:', error);
            }
            return false;
        },

        clearColumnFilters() {
            try {
                localStorage.removeItem(this.columnFiltersStorageKey);
            } catch (error) {
                console.error('Erro ao limpar filtros de coluna do localStorage:', error);
            }
        },

        openRtaDetails(rtaInfo) {
            this.rtaDetails = { ...rtaInfo };

            this.rtaDetails.formattedCreationDate = moment(this.rtaDetails.creationDate).format("YYYY-MM-DD");
            this.rtaDetails.formattedDeadline = moment(this.rtaDetails.deadline).format("YYYY-MM-DD");

            // Salva a rota de origem para o botão "Voltar"
            const returnRoute = {
                name: 'rtalist',
                params: this.$route.params.indicatorId ? { indicatorId: this.$route.params.indicatorId } : {}
            };
            sessionStorage.setItem('rtaReturnRoute', JSON.stringify(returnRoute));

            this.$router.push({ name: "rtadetails", params: { rtaId: this.rtaDetails.code, rtaData: this.rtaDetails, isManual: false } }).catch(err => console.error(err));
        },

        openRtaInNewTab(rtaInfo) {
            const rtaDetails = { ...rtaInfo };
            rtaDetails.formattedCreationDate = moment(rtaDetails.creationDate).format("YYYY-MM-DD");
            rtaDetails.formattedDeadline = moment(rtaDetails.deadline).format("YYYY-MM-DD");

            // Converte auxResponsibles de string para array se necessário
            if (typeof rtaDetails.auxResponsibles === 'string') {
                try {
                    rtaDetails.auxResponsibles = JSON.parse(rtaDetails.auxResponsibles);
                } catch (e) {
                    rtaDetails.auxResponsibles = [];
                }
            } else if (!rtaDetails.auxResponsibles) {
                rtaDetails.auxResponsibles = [];
            }

            // Salva os dados no sessionStorage para que a nova aba possa acessá-los
            sessionStorage.setItem('currentRTA', JSON.stringify(rtaDetails));

            // Salva a rota de origem para o botão "Voltar"
            const returnRoute = {
                name: 'rtalist',
                params: this.$route.params.indicatorId ? { indicatorId: this.$route.params.indicatorId } : {}
            };
            sessionStorage.setItem('rtaReturnRoute', JSON.stringify(returnRoute));

            const route = this.$router.resolve({
                name: "rtadetails",
                params: { rtaId: rtaDetails.code, rtaData: rtaDetails, isManual: false }
            });

            window.open(route.href, '_blank');
        },

        async loadRtaConfig() {
            await axios.get(`${urlMainAPI}/getGeneralConfigGerencialIndicators`)
            .then(res => {
                const cfg = res.data;
                if (cfg.actionPlanRating !== 1) {
                    this.hideCols.push('efficiencyaction');
                }

                defaultSuccess("Configurações Carregadas com Sucesso!");
            })
            .catch(err => {
                console.error(err);
                showError("Falha ao Carregar Configurações.");
            });
        },

        async loadRtaList(filters) {
            this.loadingRtaList = true;

            let url = `${urlMainAPI}/getopenrta`;
            let queryMount = [];

            if (filters.processId != null && filters.processId != undefined) queryMount.push(`processId=${filters.processId}`);
            if (filters.workshiftId != null && filters.workshiftId != undefined) queryMount.push(`workshiftId=${filters.workshiftId}`);
            if (filters.startDate != null && filters.startDate != undefined && filters.startDate != "" && filters.startDate != "Data inválida") queryMount.push(`startDate=${filters.startDate}`);
            if (filters.endDate != null && filters.endDate != undefined && filters.endDate != "" && filters.endDate != "Data inválida") queryMount.push(`endDate=${filters.endDate}`);
            if (filters.indicatorId != null && filters.indicatorId != undefined) queryMount.push(`indicatorId=${filters.indicatorId}`);
            if (queryMount.length > 0) url += `?${queryMount.join("&")}`

            await axios.get(url)
                .then(res => {
                    if (res.status == 200) {
                        // Formata as datas antes de alimentar a tabela
                        this.tableData = res.data.map(rta => {
                            const formattedRta = { ...rta };

                            // Formata data de criação - remove a hora se existir
                            if (rta.formattedCreationDate) {
                                // Se a string contém espaço (indica que tem hora), pega apenas a parte da data
                                formattedRta.formattedCreationDate = rta.formattedCreationDate.split(' ')[0];
                            }

                            // Prazo já vem formatado corretamente da API
                            // Não precisa alterar

                            return formattedRta;
                        });
                        if (res.data.length == 0) defaultSuccess('Nenhuma RTA encontrada.')
                        else defaultSuccess('RTAs Carregadas.')
                    } else {
                        this.tableData = [];
                        defaultSuccess('Nenhuma RTA encontrada.')
                    }
                })
                .catch(err => console.error(err))

            this.loadingRtaList = false;

            // Atualiza overviewFilters DEPOIS da requisição para garantir dados atualizados
            this.overviewFilters = filters;

            // Carrega filtros de coluna salvos se não houver indicatorId na rota
            if (!this.$route.params.indicatorId) {
                this.$nextTick(() => {
                    this.loadColumnFilters();
                });
            }

        },
    },

    computed: {
        ...mapState(["user"]),
    },

    async created() {
        this.validatePermissions();
        await this.loadRtaConfig();
    },

    async mounted() {
        if (this.$route.params.indicatorId) {
            this.defaultFilters.indicatorId = this.$route.params.indicatorId;
            await this.loadRtaList(this.defaultFilters);
        }
    },
}
</script>

<style scoped>
.rta-list-container {
    display: flex;
    flex-direction: column;
}

.rta-content-wrapper {
    display: flex;
    flex-direction: column;
}

.filters-and-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0.8rem 0;
}

.filters-wrapper {
    flex: 1;
}

.right-buttons {
    display: flex;
    column-gap: 0.5rem;
    font-size: 0.9rem;
    margin-left: auto;
}

.btn-cancel-selected,
.btn-export {
    display: flex;
    align-items: center;
    column-gap: 0.3rem;
    padding: 0.3rem 0.5rem;
    border-radius: 0.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.btn-cancel-selected {
    background-color: #ffe5e5;
    color: #c0392b;
    border: 1px solid #ffe5e5;
}

.btn-cancel-selected:hover:not(:disabled) {
    background-color: #ffd0d0;
    border-color: #ffd0d0;
}

.btn-cancel-selected:active:not(:disabled) {
    transform: translateY(1px);
}

.btn-cancel-selected:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-export {
    background-color: #fff0e7;
    color: #ff5f00;
    border: 1px solid #fff0e7;
}

.btn-export:hover {
    background-color: #ffe0d0;
    border-color: #ffe0d0;
}

.btn-export:active {
    transform: translateY(1px);
}

.table-section {
    margin-bottom: 0.5rem;
}

.overview-section {
    margin-top: 0.5rem;
}

.overview-section.no-data {
    margin-top: 2rem;
}

/* Força alinhamento central dos headers da tabela */
::v-deep .ve-table th {
    text-align: center !important;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.btn-confirm-cancel {
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-confirm-cancel:hover {
    background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
}

.btn-modal-close {
    padding: 0.5rem 1rem;
    background: #95a5a6;
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-modal-close:hover {
    background: #7f8c8d;
}
</style>

